<template>
  <WdsModal
    ref="delete-modal"
    :show="show"
    :z-index="4000"
    :title="$t('Delete Trusted Contact')"
    @cancel="close"
  >
    <template #default>
      <WdsAlert v-if="errorBannerMessage" error class="m-b-15">{{ errorBannerMessage }}</WdsAlert>

      <WdsStack direction-column gap-md>
        <div>
          {{ $t('Are you sure you want to delete this trusted contact?') }}
        </div>
        <WdsAlert warning icon="info">
          {{
            $t(
              'Deleting this contact will permanently remove all associated notes, custom data, and any additional information added by your company. Before deleting this contact, consider saving any important information you may need later.',
            )
          }}
        </WdsAlert>
      </WdsStack>
    </template>

    <template #footer>
      <WdsStack justify-space-between>
        <WdsButton tertiary data-test="tc-delete-cancel" @click="close">
          {{ $t('Cancel') }}
        </WdsButton>

        <WdsStack align-center gap-md>
          <WdsSpinner v-if="loading" indeterminate />

          <WdsButton
            error
            :disabled="loading"
            data-test="tc-delete-confirm"
            @click="deleteTrustedContact"
          >
            {{ $t('Delete') }}
          </WdsButton>
        </WdsStack>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script setup>
import gql from 'graphql-tag'
import { ref } from 'vue'
import { WdsAlert, WdsModal, WdsStack, WdsSpinner, WdsButton } from '@worksome/wds-components'

import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import { FormValidation } from '@/utils/formValidation'

const props = defineProps({
  trustedContactId: {
    type: [String, null],
    required: true,
  },
  show: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['cancel', 'close', 'deleted'])

const loading = ref(false)
const errorBannerMessage = ref(null)

const close = () => {
  emit('cancel')

  errorBannerMessage.value = null
}

const deleteTrustedContact = async () => {
  loading.value = true

  await publicApi
    .mutate({
      mutation: gql`
        mutation DeleteTrustedContact($input: DeleteTrustedContactInput!) {
          deleteTrustedContact(input: $input) {
            id
          }
        }
      `,
      variables: {
        input: {
          id: props.trustedContactId,
          account: currentAccount.globalId,
        },
      },
    })
    .then(() => {
      emit('close')
      emit('deleted')

      errorBannerMessage.value = null
    })
    .catch((error) => {
      const { graphQLErrors } = error

      if (FormValidation.containsErrorMessage(graphQLErrors)) {
        errorBannerMessage.value = graphQLErrors[0].message
      } else {
        errorBannerMessage.value = $t('An error occurred')
      }
    })
    .finally(() => {
      loading.value = false
    })
}
</script>
